'use client'
import 'react-lazy-load-image-component/src/effects/blur.css'
import './ImageHolder.css'

import { useRef } from 'react'
import { useMediaQuery } from 'react-responsive'

import { StylePanelProps } from '@/lib/stylePanel'
import useResponsivePadding, { ResponsivePaddingProps } from '@/lib/useResponsivePadding'
import { ImageServiceRender, imageServiceURl } from '@/utilities/imageService'
import { paddingStyleProps } from '@/utilities/padding'

export interface ImageProps {
  src: string
  alt: string
  animation?: string
  blurPlaceholder?: string
  focus?: string
}

type ItemProps = {
  decorative?: boolean
  imageStyle?: 'square' | 'rounded'
  images?: ImageProps[]
  backgroundAsset?: any
  maximumHeight?: number | null
  maximumWidth?: number | null | string
  reverseOrderMobile?: boolean
  paddingTop?: paddingStyleProps[]
  paddingBottom?: paddingStyleProps[]
  childComponent?: boolean
  disableImageMap?: boolean
  quality?: number
  maxWidthPercentage?: number
  maxWidthMobilePercentage?: number
  layout?: string
} & StylePanelProps

export const PhnxImageHolder = ({
  decorative = false,
  imageStyle,
  images,
  maximumHeight,
  maximumWidth,
  maxWidthPercentage,
  reverseOrderMobile,
  childComponent = false,
  disableImageMap = false,
  quality = 80,
  layout,
  textColor,
  backgroundColor,
  padding,
}: ItemProps) => {
  const isBigScreen = useMediaQuery({ query: '(min-width: 600px)' })
  const animRef = useRef(null)
  const mapImages = (images: ImageProps[], reverse?: boolean): any => {
    const arr = reverse ? images.slice().reverse() : images

    if (arr.length === 1) {
      // loop images and set the image src
      return arr?.map((image, index) => {
        let imageHeight = maximumHeight ? maximumHeight : null
        let imageWidth = maximumWidth ? maximumWidth : null
        let imageRender = decorative
          ? ImageServiceRender(
              image.src,
              '',
              image.focus,
              imageHeight,
              imageWidth,
              null,
              disableImageMap
            )
          : ImageServiceRender(
              image.src,
              image.alt,
              image.focus,
              imageHeight,
              imageWidth,
              null,
              disableImageMap
            )

        if (image.animation === 'tilt') {
          return (
            <img
              ref={animRef}
              src={image.src + `/m/`}
              sizes="400px 600px 800px 1050px 1800px"
              alt={decorative ? '' : image.alt}
              key={`DAHTilt_${index}`}
            />
          )
        }

        if (image.src?.includes('.svg')) {
          return (
            <img
              key={`DAHImg_${image.src}`}
              src={image.src}
              alt={decorative ? '' : image.alt}
              className={imageStyle == 'rounded' ? 'border-radius' : ''}
              aria-hidden={decorative ? true : false}
            />
          )
        }

        if (!image.src?.includes('.svg')) {
          return (
            <div className="flex-image" key={`DAHsvg_${image.src}_${imageWidth}`}>
              {imageRender}
            </div>
          )
        }
      })
    } else {
      if (arr.length != 0) {
        return arr?.map((image, index) => (
          <div className="flex-image" key={`DAHArray_${index}`}>
            {image.src.includes('.svg') && (
              <img
                src={image.src}
                alt={decorative ? '' : image.alt}
                className={imageStyle == 'rounded' ? 'border-radius' : ''}
                aria-hidden={decorative ? true : false}
              />
            )}
            {!image.src.includes('.svg') && (
              <picture className={imageStyle == 'rounded' ? 'border-radius' : ''}>
                {!childComponent && (
                  <>
                    {[2500, 1800, 1050].map((width) => {
                      // if maximumHeight is set, use it to calculate the height else use the default height

                      let imageHeight = maximumHeight ? maximumHeight : null

                      let imageUrl = imageServiceURl(image.src, imageHeight, width, image.focus)

                      // if width is 600 or 400 add don't add height to the url
                      if (width === 600 || width === 350) {
                        imageUrl = imageServiceURl(image.src, 0, width, image.focus)
                      }

                      return (
                        <source
                          key={`DAHnonsvg_${index}_${imageUrl.imgURL}`}
                          srcSet={imageUrl.imgURL}
                          media={`(min-width: ${width}px)`}
                        />
                      )
                    })}
                  </>
                )}
                <img
                  data-src={
                    image.src +
                    `/m/2500x${maximumHeight ? maximumHeight : 0}/filters:focal(${
                      image.focus ? image.focus : ''
                    })`
                  }
                  src={
                    image.src +
                    `/m/2500x${maximumHeight ? maximumHeight : 0}/filters:focal(${
                      image.focus ? image.focus : ''
                    }):blur(${maximumHeight && maximumHeight < 100 ? 2 : 10}):quality(${maximumHeight && maximumHeight < 100 ? 95 : quality})`
                  }
                  loading={maximumHeight && maximumHeight < 100 ? 'eager' : 'lazy'}
                  alt={decorative ? '' : image.alt}
                />
              </picture>
            )}
          </div>
        ))
      }
    }
  }

  const responsivePadding: ResponsivePaddingProps = useResponsivePadding(padding)

  const style = {
    ...responsivePadding,
    backgroundColor,
    color: textColor,
    maxHeight: maximumHeight ? maximumHeight + 'px' : 'auto',
    overflow: 'hidden',
    maxWidth: maxWidthPercentage ? (isBigScreen ? `${maxWidthPercentage}%` : '100%') : '100%',
  }

  return (
    <div className={`PhnxImageHolder ${layout}`} style={style}>
      {images && (reverseOrderMobile && !isBigScreen ? mapImages(images, true) : mapImages(images))}
    </div>
  )
}
